import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Plus, Minus, Search, ArrowUpDown, MoreVertical, GripVertical, Music4, ListMusic, Library as LibraryIcon, FolderPlus, Pencil, Trash2, Eye, Heart, Check, Lock, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ChordSheetViewer from "@/components/ChordSheetViewer";
import { api, apiError } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const NOTES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const FLAT = { Db: "C#", Eb: "D#", Gb: "F#", Ab: "G#", Bb: "A#" };
export function transposeKey(key, steps) {
  if (!key) return key;
  const m = key.trim().match(/^([A-G])(#|b)?(.*)$/);
  if (!m) return key;
  let name = m[1] + (m[2] || "");
  if (FLAT[name]) name = FLAT[name];
  const idx = NOTES.indexOf(name);
  if (idx < 0) return key;
  return NOTES[(idx + steps + 12) % 12] + (m[3] || "");
}
const toEntry = (s) => ({ title: s.title, artist: s.artist || "", key: s.key || "", lyrics: s.lyrics || "", notes: s.notes || "", song_id: s.id, structured: s.structured });
export const sortList = (list, mode) => {
  if (mode === "title") return [...list].sort((a, b) => (a.title || "").localeCompare(b.title || ""));
  if (mode === "artist") return [...list].sort((a, b) => (a.artist || "").localeCompare(b.artist || ""));
  if (mode === "key") return [...list].sort((a, b) => (a.key || "").localeCompare(b.key || ""));
  return list;
};
const SORT_LABEL = { manual: "Manual (arrastar)", title: "Título A-Z", artist: "Artista", key: "Tom" };

export const KeyBadge = ({ k }) => (k ? <span className="shrink-0 text-[11px] font-semibold px-2 py-1 rounded-md bg-accent/10 border border-accent/30 text-accent font-mono">{k}</span> : <span className="shrink-0 w-8" />);

export function OrdenarMenu({ value, onChange, keys = ["manual", "title", "artist", "key"], testid }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid={testid} className="rounded-xl gap-1.5 bg-transparent shrink-0"><ArrowUpDown className="h-4 w-4" />Ordenar</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {keys.map((k) => (
          <DropdownMenuItem key={k} data-testid={`${testid}-${k}`} onClick={() => onChange(k)} className="gap-2">
            {value === k && <Check className="h-4 w-4 text-accent" />}<span className={value === k ? "font-semibold" : ""}>{SORT_LABEL[k]}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SongRowAll({ s, index, draggable, onOpen, onSetlist, onTranspose, onDelete, inSetlist }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 20 : undefined };
  return (
    <div ref={setNodeRef} style={style} data-testid={`library-song-row-${s.id}`} className="group flex items-center gap-2 sm:gap-3 rounded-xl border border-border bg-surface px-3 py-3 hover:border-accent/40 transition-colors">
      {draggable ? (
        <button {...attributes} {...listeners} data-testid={`library-drag-${s.id}`} aria-label="Arrastar para reordenar" className="touch-none cursor-grab active:cursor-grabbing text-muted-foreground/60 hover:text-foreground shrink-0"><GripVertical className="h-5 w-5" /></button>
      ) : <span className="w-5 shrink-0" />}
      <span className="w-6 text-center text-sm font-semibold text-muted-foreground shrink-0 tabular-nums">{index + 1}</span>
      <button onClick={onOpen} data-testid={`library-song-open-${s.id}`} className="min-w-0 flex-1 text-left">
        <div className="font-semibold text-[15px] truncate">{s.title}</div>
        {s.artist && <div className="text-xs text-muted-foreground truncate">{s.artist}</div>}
      </button>
      <KeyBadge k={s.key} />
      <span data-testid={`library-setlist-flag-${s.id}`} title={inSetlist ? "Está em pelo menos um setlist" : "Não está em nenhum setlist"} aria-label={inSetlist ? "Em setlist" : "Fora de setlist"} className="h-8 w-8 grid place-items-center shrink-0"><Heart className={`h-4 w-4 ${inSetlist ? "text-red-500 fill-current" : "text-muted-foreground"}`} /></span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={`library-song-menu-${s.id}`} aria-label="Mais ações" className="h-8 w-8 grid place-items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-surface-hover shrink-0"><MoreVertical className="h-4 w-4" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem onClick={onOpen} data-testid={`lib-menu-view-${s.id}`} className="gap-2"><Eye className="h-4 w-4" />Ver cifra</DropdownMenuItem>
          {onSetlist && <DropdownMenuItem onClick={onSetlist} data-testid={`lib-menu-setlist-${s.id}`} className="gap-2"><FolderPlus className="h-4 w-4" />Adicionar a setlist</DropdownMenuItem>}
          <DropdownMenuItem onClick={() => onTranspose(1)} data-testid={`lib-menu-up-${s.id}`} className="gap-2"><Plus className="h-4 w-4" />Subir ½ tom</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onTranspose(-1)} data-testid={`lib-menu-down-${s.id}`} className="gap-2"><Minus className="h-4 w-4" />Descer ½ tom</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onDelete} data-testid={`lib-menu-del-${s.id}`} className="gap-2 text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" />Excluir da biblioteca</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function SetlistRow({ id, s, index, onOpen, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 20 : undefined };
  return (
    <div ref={setNodeRef} style={style} data-testid={`setlist-row-${index}`} className="group flex items-center gap-2 sm:gap-3 rounded-xl border border-border bg-surface px-3 py-3 hover:border-accent/40 transition-colors">
      <button {...attributes} {...listeners} data-testid={`setlist-row-drag-${index}`} aria-label="Arrastar para reordenar" className="touch-none cursor-grab active:cursor-grabbing text-muted-foreground/60 hover:text-foreground shrink-0"><GripVertical className="h-5 w-5" /></button>
      <span className="w-6 text-center text-sm font-semibold text-muted-foreground shrink-0 tabular-nums">{index + 1}</span>
      <button onClick={onOpen} data-testid={`setlist-row-open-${index}`} className="min-w-0 flex-1 text-left">
        <div className="font-semibold text-[15px] truncate">{s.title || "Sem título"}</div>
        {s.artist && <div className="text-xs text-muted-foreground truncate">{s.artist}</div>}
      </button>
      <KeyBadge k={s.key} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={`setlist-row-menu-${index}`} aria-label="Mais ações" className="h-8 w-8 grid place-items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-surface-hover shrink-0"><MoreVertical className="h-4 w-4" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={onOpen} data-testid={`setlist-row-view-${index}`} className="gap-2"><Eye className="h-4 w-4" />Ver cifra</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onRemove} data-testid={`setlist-row-remove-${index}`} className="gap-2 text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" />Remover do setlist</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function Library() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [songs, setSongs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "setlists" ? "setlists" : "all");
  const [q, setQ] = useState("");
  const [sortAll, setSortAll] = useState("manual");
  const [sel, setSel] = useState(null); // setlist em detalhe
  const [dq, setDq] = useState("");
  const [dsort, setDsort] = useState("manual");
  // modais
  const [setlistSong, setSetlistSong] = useState(null); // música da aba "todas" p/ adicionar a setlist
  const [checkedSets, setCheckedSets] = useState({});
  const [newSetName, setNewSetName] = useState("");
  const [savingSet, setSavingSet] = useState(false);
  const [addSongsOpen, setAddSongsOpen] = useState(false);
  const [addChecks, setAddChecks] = useState({});
  const [editGroup, setEditGroup] = useState(null); // { id?, name, description }
  const [viewer, setViewer] = useState(null); // música legada sem song_id

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const loadSongs = () => api.get("/songs").then((r) => setSongs(r.data)).catch(() => {});
  const loadGroups = () => api.get("/repertoires").then((r) => setGroups(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/me/plan").then((r) => {
      setPlan(r.data);
      if (r.data?.repertoire_access?.allowed !== false) { loadSongs(); loadGroups(); }
    }).catch(() => setPlan({ repertoire_access: { allowed: true }, repertoire_limits: {} }));
  }, []);

  const access = plan?.repertoire_access;
  const limits = plan?.repertoire_limits || {};
  const groupLim = limits.rep_groups_limit;
  const atGroupLim = groupLim >= 0 && groups.length >= groupLim;

  const filteredAll = useMemo(() => {
    const base = songs.filter((s) => !q || s.title.toLowerCase().includes(q.toLowerCase()) || (s.artist || "").toLowerCase().includes(q.toLowerCase()));
    return sortList(base, sortAll);
  }, [songs, q, sortAll]);
  const allDraggable = sortAll === "manual" && !q;
  const setlistSongIds = useMemo(() => { const set = new Set(); (groups || []).forEach((g) => (g.songs || []).forEach((x) => { if (x.song_id) set.add(x.song_id); })); return set; }, [groups]);

  const detailRows = useMemo(() => {
    if (!sel) return [];
    return (sel.songs || []).map((s, idx) => ({ s, idx })).filter(({ s }) => !dq || (s.title || "").toLowerCase().includes(dq.toLowerCase()) || (s.artist || "").toLowerCase().includes(dq.toLowerCase()));
  }, [sel, dq]);
  const detailDraggable = !dq;

  // ---- ações músicas (aba todas) ----
  const upd = (id, k, v) => setSongs((p) => p.map((s) => (s.id === id ? { ...s, [k]: v } : s)));
  const openSong = (id, ids, ctx) => navigate(`/repertorio/musica/${id}`, ids && ids.length ? { state: { nav: { ids, ctx } } } : undefined);
  const transpose = async (s, delta) => {
    const nk = transposeKey(s.key, delta); upd(s.id, "key", nk);
    try { await api.put(`/songs/${s.id}`, { title: s.title, artist: s.artist || "", key: nk, lyrics: s.lyrics || "", chords: s.chords || "", notes: s.notes || "", category: s.category || "", tags: s.tags || [] }); }
    catch (e) { upd(s.id, "key", s.key); toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleFav = async (s) => {
    const next = !s.favorite; upd(s.id, "favorite", next);
    try { await api.patch(`/songs/${s.id}/favorite`, { favorite: next }); toast.success(next ? "Adicionada à biblioteca!" : "Removida da biblioteca."); }
    catch (e) { upd(s.id, "favorite", !next); toast.error(apiError(e.response?.data?.detail)); }
  };
  const delSong = async (id) => { try { await api.delete(`/songs/${id}`); toast.success("Removida."); loadSongs(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const onDragAll = ({ active, over }) => {
    if (!over || active.id === over.id || !allDraggable) return;
    const oldI = songs.findIndex((x) => x.id === active.id);
    const newI = songs.findIndex((x) => x.id === over.id);
    if (oldI < 0 || newI < 0) return;
    const next = arrayMove(songs, oldI, newI); setSongs(next);
    api.put("/songs/order", { ids: next.map((s) => s.id) }).catch(() => {});
  };

  // ---- ações setlists ----
  const putSetlist = async (g, newSongs) => {
    await api.put(`/repertoires/${g.id}`, { name: g.name, description: g.description || "", songs: newSongs });
    const updated = { ...g, songs: newSongs };
    setSel(updated);
    setGroups((p) => p.map((x) => (x.id === g.id ? updated : x)));
  };
  const onDragDetail = async ({ active, over }) => {
    if (!over || active.id === over.id || !detailDraggable || !sel) return;
    const next = arrayMove(sel.songs, Number(active.id), Number(over.id));
    try { await putSetlist(sel, next); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const applyDetailSort = async (mode) => {
    setDsort(mode);
    if (mode === "manual" || !sel) return;
    try { await putSetlist(sel, sortList(sel.songs, mode)); toast.success("Setlist reordenado."); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const removeFromSetlist = async (index) => {
    try { await putSetlist(sel, sel.songs.filter((_, x) => x !== index)); toast.success("Removida do setlist."); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openSetlistRow = (s) => {
    if (s.song_id) {
      const ids = (sel?.songs || []).filter((x) => x.song_id).map((x) => x.song_id);
      navigate(`/repertorio/musica/${s.song_id}`, { state: { nav: { ids, ctx: sel?.name || "Setlist" } } });
    } else if (s.structured || s.lyrics || s.chords) setViewer(s);
    else toast.error("Esta música não tem cifra vinculada.");
  };
  const confirmAddSongs = async () => {
    const ids = Object.keys(addChecks).filter((k) => addChecks[k]);
    if (!ids.length) return toast.error("Selecione ao menos uma música.");
    try {
      const entries = ids.map((id) => toEntry(songs.find((s) => s.id === id))).filter(Boolean);
      await putSetlist(sel, [...(sel.songs || []), ...entries]);
      toast.success(`${entries.length} adicionada(s) ao setlist!`);
      setAddSongsOpen(false); setAddChecks({});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveGroup = async () => {
    if (!editGroup.name.trim()) return toast.error("Informe o nome do setlist.");
    const body = { name: editGroup.name.trim(), description: editGroup.description || "", songs: editGroup.songs || [] };
    try {
      if (editGroup.id) { await api.put(`/repertoires/${editGroup.id}`, body); toast.success("Setlist atualizado!"); }
      else { await api.post("/repertoires", body); toast.success("Setlist criado!"); }
      setEditGroup(null); loadGroups();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delGroup = async (id) => { try { await api.delete(`/repertoires/${id}`); toast.success("Setlist removido."); if (sel?.id === id) setSel(null); loadGroups(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  // ---- modal "adicionar a setlist" (aba todas) ----
  const openSetlistFor = (s) => { setSetlistSong(s); setCheckedSets({}); setNewSetName(""); };
  const confirmSetlistFor = async () => {
    const ids = Object.keys(checkedSets).filter((k) => checkedSets[k]);
    if (!ids.length && !newSetName.trim()) return toast.error("Escolha um setlist ou crie um novo.");
    setSavingSet(true);
    try {
      const entry = toEntry(setlistSong);
      for (const rid of ids) await api.post(`/repertoires/${rid}/songs`, entry);
      if (newSetName.trim()) await api.post("/repertoires", { name: newSetName.trim(), songs: [entry] });
      toast.success("Adicionada ao setlist!");
      setSetlistSong(null); loadGroups();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingSet(false); }
  };

  if (!plan) return <div className="text-muted-foreground" data-testid="library-loading">Carregando...</div>;
  if (access && access.allowed === false) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="library-locked">
        <div className="bg-surface border border-border rounded-lg p-8 text-center flex flex-col items-center gap-4">
          <div className="h-14 w-14 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center"><Lock className="h-7 w-7 text-primary" /></div>
          <h1 className="font-head text-2xl font-bold">Biblioteca indisponível no momento</h1>
          <p className="text-muted-foreground text-sm">No Plano Gratuito, o Repertório fica acessível apenas enquanto você tiver um evento em aberto. Seus setlists e músicas <b>não foram apagados</b>.</p>
          <div className="flex gap-2">
            <Button data-testid="library-locked-plans" onClick={() => navigate("/meu-plano")} className="rounded-md gap-2"><Crown className="h-4 w-4" />Ver planos</Button>
            <Button variant="outline" onClick={() => navigate("/repertorio")} className="rounded-md bg-transparent">Voltar ao repertório</Button>
          </div>
        </div>
      </div>
    );
  }

  const inp = "bg-background border-border rounded-xl";

  // ===== DETALHE DE SETLIST =====
  if (sel) {
    return (
      <div className="max-w-3xl mx-auto space-y-5" data-testid="setlist-detail">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setSel(null)} data-testid="setlist-back" className="rounded-xl gap-2 bg-transparent shrink-0"><ArrowLeft className="h-4 w-4" />Voltar</Button>
          <div className="min-w-0 flex-1">
            <h1 className="font-head text-xl sm:text-2xl font-extrabold truncate">{sel.name}</h1>
            <p className="text-xs text-muted-foreground">{(sel.songs || []).length} música{(sel.songs || []).length !== 1 ? "s" : ""}</p>
          </div>
          <Button onClick={() => { setAddChecks({}); setAddSongsOpen(true); }} data-testid="setlist-add-song" className="rounded-xl gap-2 shrink-0"><Plus className="h-4 w-4" />Adicionar</Button>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="setlist-detail-search" value={dq} onChange={(e) => setDq(e.target.value)} placeholder="Buscar cifra na setlist..." className={`${inp} pl-9`} />
          </div>
          <OrdenarMenu value={dsort} onChange={applyDetailSort} testid="setlist-detail-sort" />
        </div>

        {(sel.songs || []).length === 0 ? (
          <div className="text-muted-foreground bg-surface border border-dashed border-border rounded-xl p-12 text-center flex flex-col items-center gap-2"><ListMusic className="h-8 w-8 opacity-60" />Nenhuma música neste setlist. Toque em "Adicionar".</div>
        ) : detailRows.length === 0 ? (
          <div className="text-muted-foreground bg-surface border border-dashed border-border rounded-xl p-8 text-center">Nenhuma música corresponde à busca.</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragDetail}>
            <SortableContext items={detailRows.map((r) => String(r.idx))} strategy={verticalListSortingStrategy}>
              <div className="space-y-2" data-testid="setlist-rows">
                {detailRows.map(({ s, idx }) => (
                  <SetlistRow key={idx} id={String(idx)} s={s} index={idx} onOpen={() => openSetlistRow(s)} onRemove={() => removeFromSetlist(idx)} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {/* Modal adicionar músicas da biblioteca */}
        <Dialog open={addSongsOpen} onOpenChange={setAddSongsOpen}>
          <DialogContent className="max-w-md" data-testid="add-songs-modal">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5 text-accent" />Adicionar músicas</DialogTitle><DialogDescription>Escolha músicas da sua biblioteca para incluir neste setlist.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              {songs.length === 0 ? <p className="text-sm text-muted-foreground">Sua biblioteca está vazia.</p> : (
                <div className="max-h-72 overflow-y-auto space-y-1.5">
                  {songs.map((s) => (
                    <label key={s.id} data-testid={`add-song-item-${s.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border bg-surface hover:border-accent/40 cursor-pointer transition-colors">
                      <Checkbox data-testid={`add-song-check-${s.id}`} checked={!!addChecks[s.id]} onCheckedChange={(v) => setAddChecks((c) => ({ ...c, [s.id]: !!v }))} />
                      <span className="min-w-0 flex-1"><span className="block font-medium text-sm truncate">{s.title}</span>{s.artist && <span className="block text-xs text-muted-foreground truncate">{s.artist}</span>}</span>
                      <KeyBadge k={s.key} />
                    </label>
                  ))}
                </div>
              )}
              <Button data-testid="add-songs-confirm" onClick={confirmAddSongs} className="w-full rounded-xl gap-2"><Check className="h-4 w-4" />Adicionar selecionadas</Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="setlist-song-viewer">
            <DialogHeader><DialogTitle>{viewer?.title}{viewer?.key ? ` · Tom ${viewer.key}` : ""}</DialogTitle></DialogHeader>
            {viewer?.structured ? <ChordSheetViewer structured={viewer.structured} music={viewer.title} artist={viewer.artist} /> : <pre className="whitespace-pre-wrap font-mono text-sm text-foreground">{viewer?.chords || viewer?.lyrics || "Sem conteúdo."}</pre>}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ===== BIBLIOTECA (abas) =====
  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="library-page">
      <section className="relative overflow-hidden rounded-2xl border border-accent/20 bg-gradient-to-br from-accent/10 via-card to-card p-5 sm:p-7">
        <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-accent/10 blur-3xl" aria-hidden="true" />
        <div className="relative flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate("/repertorio")} data-testid="library-back" className="rounded-xl gap-2 bg-transparent border-white/15 shrink-0"><ArrowLeft className="h-4 w-4" />Voltar</Button>
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-accent/15 text-accent shrink-0"><LibraryIcon className="h-6 w-6" /></span>
          <div className="min-w-0">
            <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight">Bib<span className="bg-gradient-to-r from-primary to-amber-400 bg-clip-text text-transparent">lioteca</span></h1>
            <p className="text-sm text-muted-foreground mt-0.5">Gerencie suas músicas e setlists em poucos cliques.</p>
          </div>
        </div>
      </section>

      <div className="flex items-center gap-2 p-1 rounded-2xl border border-border bg-surface w-full sm:w-auto sm:inline-flex">
        <button data-testid="library-tab-all" onClick={() => setTab("all")} className={`flex-1 sm:flex-none px-4 py-2 rounded-xl text-sm font-semibold gap-2 inline-flex items-center justify-center transition-colors ${tab === "all" ? "bg-accent/15 text-accent" : "text-muted-foreground hover:text-foreground"}`}><Music4 className="h-4 w-4" />Todas as músicas <span className="text-xs opacity-70">({songs.length})</span></button>
        <button data-testid="library-tab-setlists" onClick={() => setTab("setlists")} className={`flex-1 sm:flex-none px-4 py-2 rounded-xl text-sm font-semibold gap-2 inline-flex items-center justify-center transition-colors ${tab === "setlists" ? "bg-accent/15 text-accent" : "text-muted-foreground hover:text-foreground"}`}><ListMusic className="h-4 w-4" />Setlists <span className="text-xs opacity-70">({groups.length})</span></button>
      </div>

      {tab === "all" && (
        <div className="space-y-3" data-testid="library-all">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input data-testid="library-search-all" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por título ou artista..." className={`${inp} pl-9`} />
            </div>
            <OrdenarMenu value={sortAll} onChange={setSortAll} testid="library-sort-all" />
          </div>
          {filteredAll.length === 0 ? (
            <div className="text-muted-foreground bg-surface border border-dashed border-border rounded-xl p-12 text-center flex flex-col items-center gap-2"><Music4 className="h-8 w-8 opacity-60" />{q ? "Nenhuma música corresponde à busca." : "Nenhuma música na sua biblioteca ainda."}</div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragAll}>
              <SortableContext items={filteredAll.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2" data-testid="library-all-rows">
                  {filteredAll.map((s, i) => (
                    <SongRowAll key={s.id} s={s} index={i} draggable={allDraggable}
                      onOpen={() => openSong(s.id, filteredAll.map((x) => x.id), "Biblioteca")} onSetlist={() => openSetlistFor(s)} inSetlist={setlistSongIds.has(s.id)}
                      onTranspose={(d) => transpose(s, d)} onDelete={() => delSong(s.id)} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}

      {tab === "setlists" && (
        <div className="space-y-3" data-testid="library-setlists">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">Monte setlists e reutilize em qualquer evento. Uma música pode estar em vários setlists.</p>
            <Button data-testid="library-new-setlist" onClick={() => setEditGroup({ name: "", description: "", songs: [] })} disabled={atGroupLim} className="rounded-xl gap-2 shrink-0 disabled:opacity-40"><FolderPlus className="h-4 w-4" />Novo setlist</Button>
          </div>
          {atGroupLim && <p className="text-xs text-destructive flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" />Você atingiu o limite de {groupLim} setlist(s) do seu plano.</p>}
          {groups.length === 0 ? (
            <div className="text-muted-foreground bg-surface border border-dashed border-border rounded-xl p-12 text-center flex flex-col items-center gap-2"><ListMusic className="h-8 w-8 opacity-60" />Nenhum setlist ainda. Crie o primeiro.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="setlist-cards">
              {groups.map((g) => (
                <div key={g.id} data-testid={`setlist-card-${g.id}`} className="group rounded-2xl border border-border bg-surface p-4 hover:border-accent/50 transition-colors flex items-center gap-3">
                  <button onClick={() => { setSel(g); setDq(""); setDsort("manual"); }} data-testid={`setlist-open-${g.id}`} className="min-w-0 flex-1 text-left flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-accent/15 text-accent shrink-0"><ListMusic className="h-5 w-5" /></span>
                    <span className="min-w-0"><span className="block font-head font-semibold truncate">{g.name}</span><span className="block text-xs text-muted-foreground">{(g.songs || []).length} música(s){g.description ? ` · ${g.description}` : ""}</span></span>
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`setlist-menu-${g.id}`} aria-label="Ações do setlist" className="h-8 w-8 grid place-items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-surface-hover shrink-0"><MoreVertical className="h-4 w-4" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem onClick={() => { setSel(g); setDq(""); setDsort("manual"); }} data-testid={`setlist-open-menu-${g.id}`} className="gap-2"><Eye className="h-4 w-4" />Abrir</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setEditGroup({ id: g.id, name: g.name, description: g.description || "", songs: g.songs || [] })} data-testid={`setlist-edit-${g.id}`} className="gap-2"><Pencil className="h-4 w-4" />Renomear</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => delGroup(g.id)} data-testid={`setlist-del-${g.id}`} className="gap-2 text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" />Excluir</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal: adicionar música (aba todas) a setlist(s) */}
      <Dialog open={!!setlistSong} onOpenChange={(o) => !o && setSetlistSong(null)}>
        <DialogContent className="max-w-md" data-testid="cs-setlist-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FolderPlus className="h-5 w-5 text-accent" />Adicionar a setlist</DialogTitle><DialogDescription className="truncate">{setlistSong?.title}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {groups.length > 0 ? (
              <div className="max-h-56 overflow-y-auto space-y-1.5">
                {groups.map((g) => (
                  <label key={g.id} data-testid={`cs-setlist-item-${g.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border bg-surface hover:border-accent/40 cursor-pointer transition-colors">
                    <Checkbox data-testid={`cs-setlist-check-${g.id}`} checked={!!checkedSets[g.id]} onCheckedChange={(v) => setCheckedSets((c) => ({ ...c, [g.id]: !!v }))} />
                    <span className="min-w-0 flex-1"><span className="block font-medium text-sm truncate">{g.name}</span><span className="block text-xs text-muted-foreground">{(g.songs || []).length} música(s)</span></span>
                  </label>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">Você ainda não tem setlists. Crie um abaixo.</p>}
            <div className="flex items-center gap-2 border-t border-border pt-3">
              <Plus className="h-4 w-4 text-primary shrink-0" />
              <Input data-testid="cs-create-setlist-name" value={newSetName} onChange={(e) => setNewSetName(e.target.value)} placeholder="Criar novo setlist (ex: Pagode 90)" className="bg-background border-border rounded-md" />
            </div>
            <Button data-testid="cs-setlist-save-confirm" onClick={confirmSetlistFor} disabled={savingSet} className="w-full rounded-xl gap-2"><Check className="h-4 w-4" />Salvar no setlist</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: criar / renomear setlist */}
      <Dialog open={!!editGroup} onOpenChange={(o) => !o && setEditGroup(null)}>
        <DialogContent className="max-w-md" data-testid="setlist-edit-modal">
          <DialogHeader><DialogTitle>{editGroup?.id ? "Renomear setlist" : "Novo setlist"}</DialogTitle></DialogHeader>
          {editGroup && (
            <div className="space-y-3">
              <div><label className="text-xs text-muted-foreground">Nome *</label><Input data-testid="setlist-edit-name" value={editGroup.name} onChange={(e) => setEditGroup({ ...editGroup, name: e.target.value })} placeholder="Ex: Pagode 90" className={inp} /></div>
              <div><label className="text-xs text-muted-foreground">Descrição (opcional)</label><Input data-testid="setlist-edit-desc" value={editGroup.description} onChange={(e) => setEditGroup({ ...editGroup, description: e.target.value })} placeholder="Ex: repertório de sábado" className={inp} /></div>
              <Button data-testid="setlist-edit-save" onClick={saveGroup} className="w-full rounded-xl gap-2"><Check className="h-4 w-4" />{editGroup.id ? "Salvar" : "Criar setlist"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
